import { PerspectiveCamera, Vector3 } from "three";

/** Visual tuning for the chase camera; not gameplay numbers. */
const CAMERA = {
  /** Distance behind the car at rest and extra per m/s. */
  distance: 7.2,
  distancePerSpeed: 0.035,
  height: 2.5,
  lookAhead: 5,
  lookHeight: 1.0,
  /** Time constants (s): heading follow, and position lag. */
  yawLag: 0.28,
  positionLag: 0.07,
  /** How far the camera heading swings from the car's heading towards its velocity (0..1). */
  driftSwing: 0.7,
  fovMin: 62,
  fovMax: 80,
  fovSpeed: 60,
};

/** Chase camera that lags behind the car and swings towards the direction of travel in a drift. */
export class ChaseCamera {
  readonly camera = new PerspectiveCamera(CAMERA.fovMin, 1, 0.1, 1200);
  private yaw = 0;
  private readonly pos = new Vector3();
  private readonly look = new Vector3();
  private snapped = false;

  /** Jump straight to the target next update (after a reset). */
  snap(): void {
    this.snapped = false;
  }

  /**
   * @param yaw car heading; `slip` body slip angle (velocity direction relative to heading);
   * `speed` in m/s; `dt` the render frame time in seconds.
   */
  update(
    x: number,
    y: number,
    z: number,
    yaw: number,
    slip: number,
    speed: number,
    dt: number,
  ): void {
    const targetYaw = yaw + slip * CAMERA.driftSwing;
    if (!this.snapped) {
      this.yaw = targetYaw;
    } else {
      let diff = targetYaw - this.yaw;
      diff -= Math.PI * 2 * Math.round(diff / (Math.PI * 2));
      this.yaw += diff * (1 - Math.exp(-dt / CAMERA.yawLag));
    }

    const back = CAMERA.distance + speed * CAMERA.distancePerSpeed;
    const sy = Math.sin(this.yaw);
    const cy = Math.cos(this.yaw);
    const wantX = x - sy * back;
    const wantZ = z - cy * back;
    const wantY = y + CAMERA.height;

    if (!this.snapped) {
      this.pos.set(wantX, wantY, wantZ);
      this.snapped = true;
    } else {
      const k = 1 - Math.exp(-dt / CAMERA.positionLag);
      this.pos.x += (wantX - this.pos.x) * k;
      this.pos.y += (wantY - this.pos.y) * k;
      this.pos.z += (wantZ - this.pos.z) * k;
    }

    this.look.set(x + sy * CAMERA.lookAhead, y + CAMERA.lookHeight, z + cy * CAMERA.lookAhead);
    this.camera.position.copy(this.pos);
    this.camera.lookAt(this.look);

    const fov =
      CAMERA.fovMin + (CAMERA.fovMax - CAMERA.fovMin) * Math.min(speed / CAMERA.fovSpeed, 1);
    if (Math.abs(fov - this.camera.fov) > 0.05) {
      this.camera.fov = fov;
      this.camera.updateProjectionMatrix();
    }
  }

  resize(width: number, height: number): void {
    this.camera.aspect = width / Math.max(height, 1);
    this.camera.updateProjectionMatrix();
  }
}
